import React, { useEffect, useState } from 'react';
import MetricChart from './MetricChart';
import { MEASUREMENTS, loadEntries, addEntry, deleteEntry, loadProfile, saveProfile } from './api';
import './fitness.css';

// Height is constant for an adult, so it's logged but not charted.
const CHARTED = MEASUREMENTS.filter(m => m.key !== 'height');

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDay = (date) =>
  new Date(`${date}T00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const series = (entries, key) => entries.filter(e => Number.isFinite(e[key]));
const latest = (entries, key) => {
  const s = series(entries, key);
  return s.length ? s[s.length - 1][key] : null;
};
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n).toFixed(1)}`;

const emptyForm = (entries) => ({
  date: today(),
  ...Object.fromEntries(MEASUREMENTS.map(m => [m.key, ''])),
  // Height rarely changes, so carry it forward.
  height: latest(entries, 'height') ?? '',
});

// Fixed-size trend line for the measurement list, scaled to its own range.
const Spark = ({ values }) => {
  if (values.length < 2) return <svg className="fit-spark" width="72" height="18" aria-hidden="true" />;
  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  const d = values.map((v, i) =>
    `${i ? 'L' : 'M'}${((i / (values.length - 1)) * 70 + 1).toFixed(1)},${(16 - ((v - lo) / span) * 14).toFixed(1)}`).join('');
  return <svg className="fit-spark" width="72" height="18" aria-hidden="true"><path d={d} /></svg>;
};

const Fitness = ({ user, setAppState, isDarkMode, setIsDarkMode }) => {
  const uid = user.uid;
  const [entries, setEntries] = useState([]);
  const [goalWeight, setGoalWeight] = useState(null);
  const [goalDraft, setGoalDraft] = useState('');
  const [form, setForm] = useState(emptyForm([]));
  const [metric, setMetric] = useState('weight');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadEntries(uid), loadProfile(uid)])
      .then(([loaded, profile]) => {
        if (cancelled) return;
        setEntries(loaded);
        setForm(emptyForm(loaded));
        if (Number.isFinite(profile.goalWeight)) {
          setGoalWeight(profile.goalWeight);
          setGoalDraft(String(profile.goalWeight));
        }
      })
      .catch(err => !cancelled && setError(`Couldn't load fitness data: ${err.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [uid]);

  const saveGoal = async () => {
    const value = parseFloat(goalDraft);
    if (!Number.isFinite(value) || value <= 0) {
      setGoalDraft(goalWeight != null ? String(goalWeight) : '');
      return;
    }
    if (value === goalWeight) return;
    setError('');
    try {
      await saveProfile(uid, { goalWeight: value });
      setGoalWeight(value);
    } catch (err) {
      setError(`Couldn't save goal: ${err.message}`);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    const entry = { date: form.date };
    MEASUREMENTS.forEach(m => {
      const v = parseFloat(form[m.key]);
      if (Number.isFinite(v) && v > 0) entry[m.key] = v;
    });
    if (Object.keys(entry).length === 1) {
      setError('Enter at least one measurement.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await addEntry(uid, entry);
      const next = [...entries, saved].sort((a, b) =>
        a.date.localeCompare(b.date) || (a.createdAt || 0) - (b.createdAt || 0));
      setEntries(next);
      setForm(emptyForm(next));
    } catch (err) {
      setError(`Couldn't save entry: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setError('');
    try {
      await deleteEntry(uid, id);
      setEntries(entries.filter(e => e.id !== id));
    } catch (err) {
      setError(`Couldn't delete entry: ${err.message}`);
    }
  };

  const weights = series(entries, 'weight');
  const currentWeight = latest(entries, 'weight');
  const toGo = currentWeight != null && goalWeight != null ? goalWeight - currentWeight : null;
  const active = CHARTED.find(m => m.key === metric);
  const lastEntry = entries[entries.length - 1];

  return (
    <div className={`fit ${isDarkMode ? 'fit-dark' : 'fit-light'}`}>
      <header className="fit-top">
        <h1>fit</h1>
        <nav className="fit-actions">
          <button onClick={() => setAppState('planner')}>planner</button>
          <button onClick={() => setAppState('notes')}>notes</button>
          <button onClick={() => setIsDarkMode(!isDarkMode)}>{isDarkMode ? 'light' : 'dark'}</button>
          <button onClick={() => setAppState('main')}>esc</button>
        </nav>
      </header>

      {error && (
        <p className="fit-error">
          {error}
          {/permission/i.test(error) && ` (signed in as ${user.email || 'an account with no email'})`}
        </p>
      )}

      <section className="fit-stats">
        <div className="fit-stat">
          <span className="fit-label">current</span>
          <b>{currentWeight ?? '—'}<small> lb</small></b>
          <span className="fit-muted">
            {weights.length > 1
              ? `${signed(currentWeight - weights[0].weight)} since ${fmtDay(weights[0].date)}`
              : lastEntry ? `logged ${fmtDay(lastEntry.date)}` : 'nothing logged'}
          </span>
        </div>
        <form className="fit-stat" onSubmit={e => { e.preventDefault(); e.target.querySelector('input').blur(); }}>
          <label className="fit-label" htmlFor="fit-goal">goal</label>
          <b>
            <input id="fit-goal" className="fit-goal-input" type="number" step="0.1" min="0" placeholder="—"
              style={{ width: `${Math.max(goalDraft.length, 2) + 0.4}ch` }}
              value={goalDraft} onChange={e => setGoalDraft(e.target.value)} onBlur={saveGoal} />
            <small> lb</small>
          </b>
          <span className="fit-muted">click to edit</span>
        </form>
        <div className="fit-stat">
          <span className="fit-label">to go</span>
          <b>{toGo != null ? Math.abs(toGo).toFixed(1) : '—'}<small> lb</small></b>
          <span className="fit-muted">
            {toGo == null ? 'set a goal' : toGo === 0 ? 'at goal' : toGo < 0 ? 'to lose' : 'to gain'}
          </span>
        </div>
      </section>

      <section className="fit-chart-block">
        <div className="fit-tabs" role="tablist">
          {CHARTED.map(m => (
            <button key={m.key} role="tab" aria-selected={m.key === metric}
              className={m.key === metric ? 'on' : ''} onClick={() => setMetric(m.key)}>
              {m.label.toLowerCase()}
            </button>
          ))}
        </div>
        {loading ? (
          <p className="fit-muted fit-loading">loading…</p>
        ) : (
          <MetricChart entries={entries} metric={active.key} unit={active.unit} label={active.label}
            goal={active.key === 'weight' ? goalWeight : undefined} chartHeight={300} />
        )}
      </section>

      <div className="fit-grid">
        <article>
          <header><h3>log</h3><span className="fit-muted">lb · in</span></header>
          <form className="fit-form" onSubmit={handleAddEntry}>
            <label>
              <span>date</span>
              <input type="date" value={form.date} required onChange={e => setForm({ ...form, date: e.target.value })} />
            </label>
            {MEASUREMENTS.map(m => (
              <label key={m.key}>
                <span>{m.label.toLowerCase()}</span>
                <input type="number" step="0.1" min="0" inputMode="decimal" placeholder={m.unit}
                  value={form[m.key]} onChange={e => setForm({ ...form, [m.key]: e.target.value })} />
              </label>
            ))}
            <div className="fit-form-actions">
              <button type="submit" className="fit-btn" disabled={saving || loading}>
                {saving ? 'saving…' : 'save entry'}
              </button>
            </div>
          </form>
        </article>

        <article>
          <header><h3>measurements</h3><span className="fit-muted">latest · change</span></header>
          <table className="fit-table fit-measures">
            <tbody>
              {MEASUREMENTS.map(m => {
                const s = series(entries, m.key);
                const last = s.length ? s[s.length - 1][m.key] : null;
                const charted = m.key !== 'height';
                return (
                  <tr key={m.key} className={`${charted ? 'pick' : ''} ${m.key === metric ? 'on' : ''}`}
                    onClick={charted ? () => setMetric(m.key) : undefined}>
                    <td>{m.label.toLowerCase()}</td>
                    <td className="fit-num">{last != null ? <>{last}<span className="fit-muted"> {m.unit}</span></> : <span className="fit-muted">—</span>}</td>
                    <td className="fit-num fit-muted">{s.length > 1 ? signed(last - s[0][m.key]) : ''}</td>
                    <td className="fit-num">{charted && <Spark values={s.map(e => e[m.key])} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </article>

        <article className="fit-wide">
          <header><h3>history</h3><span className="fit-muted">{entries.length ? 'newest first' : ''}</span></header>
          {entries.length === 0 ? (
            <p className="fit-muted">{loading ? 'loading…' : 'no entries yet.'}</p>
          ) : (
            <div className="fit-scroll">
              <table className="fit-table fit-history">
                <thead>
                  <tr>
                    <th>date</th>
                    {MEASUREMENTS.map(m => <th key={m.key} className="fit-num">{m.label.toLowerCase()}</th>)}
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {[...entries].reverse().map(e => (
                    <tr key={e.id}>
                      <td>{e.date}</td>
                      {MEASUREMENTS.map(m => (
                        <td key={m.key} className={`fit-num ${e[m.key] == null ? 'fit-muted' : ''}`}>{e[m.key] ?? '—'}</td>
                      ))}
                      <td className="fit-num">
                        <button className="fit-del" onClick={() => handleDelete(e.id)}>delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default Fitness;
